import { v4 as uuid } from 'uuid';
import { openDatabase, waitForRequest, waitForTransaction } from '../indexedDB';

const DATABASE_NAME = 'pisano-feedback-queue';
const DATABASE_VERSION = 1;
const SCHEMA_VERSION = 1;
const STORE_NAME = 'records';
const OWNERSHIP_STORE_NAME = 'ownership';
const SESSION_STORE_NAME = 'sessions';
export const OWNERSHIP_DURATION_MS = 60_000;
const REQUEST_FIELDS = ['payload', 'headers', 'feedbackId', 'version', 'captcha'];
const RESULT_FIELDS = ['feedbackId', 'version', 'status', 'lastError'];
const MUTABLE_FIELDS = ['payload', 'headers', 'feedbackId', 'version', 'revision', 'captcha', 'status', 'lastError'];

function openQueueDatabase() {
  return openDatabase({
    name: DATABASE_NAME,
    version: DATABASE_VERSION,
    upgrade(database) {
      const store = database.createObjectStore(STORE_NAME, { keyPath: 'id' });
      store.createIndex('byApiUrl', 'apiUrl');
      store.createIndex('bySubmission', ['apiUrl', 'submissionId']);
      store.createIndex('bySubmissionKind', ['apiUrl', 'submissionId', 'kind'], { unique: true });
      // Keep the session owned even if its incomplete record is replaced.
      database.createObjectStore(OWNERSHIP_STORE_NAME, { keyPath: ['apiUrl', 'submissionId'] });
      const sessions = database.createObjectStore(SESSION_STORE_NAME, { keyPath: ['apiUrl', 'submissionId'] });
      sessions.createIndex('bySurvey', ['apiUrl', 'nodeId', 'flowId']);
    },
  });
}

/** The operation failed after its transaction had already committed its writes. */
export class QueueWriteCommittedError extends Error {
  constructor(cause) {
    super('Queue operation failed after its transaction committed', { cause });

    this.name = 'QueueWriteCommittedError';
  }
}

// Operations may await native requests from this transaction, never network work.
// Any other rejection means the transaction aborted and wrote nothing.
export async function withQueueStore(mode, operation, includeOwnership = false) {
  const database = await openQueueDatabase();
  let transaction;
  let completed;
  let committed = false;

  try {
    transaction = database.transaction(
      includeOwnership ? [STORE_NAME, OWNERSHIP_STORE_NAME, SESSION_STORE_NAME] : STORE_NAME,
      mode,
    );
    completed = waitForTransaction(transaction);
    // Attach a rejection handler before operation can throw synchronously.
    completed.then(
      () => {
        committed = true;
      },
      () => {},
    );
    const [result] = await Promise.all([
      operation(
        transaction.objectStore(STORE_NAME),
        includeOwnership ? transaction.objectStore(OWNERSHIP_STORE_NAME) : undefined,
        includeOwnership ? transaction.objectStore(SESSION_STORE_NAME) : undefined,
      ),
      completed,
    ]);
    return result;
  } catch (error) {
    if (transaction) {
      try {
        transaction.abort();
      } catch {
        // The transaction may already have completed or aborted.
      }
      await completed?.catch(() => {});
    }

    if (committed && mode === 'readwrite') throw new QueueWriteCommittedError(error);

    throw error;
  } finally {
    database.close();
  }
}

function validateRecord(record) {
  for (const field of ['submissionId', 'nodeId', 'flowId', 'apiUrl']) {
    if (typeof record[field] !== 'string' || !record[field].trim()) {
      throw new TypeError(`Queue record requires ${field}`);
    }
  }
  if (!['incomplete', 'final'].includes(record.kind)) {
    throw new TypeError('Invalid queue record kind');
  }
  if (!['pending', 'preparing', 'sending', 'blocked'].includes(record.status)) {
    throw new TypeError('Invalid queue record status');
  }
  if (!Number.isInteger(record.revision) || record.revision < 1) {
    throw new TypeError('Invalid queue record revision');
  }
  if (!record.payload || typeof record.payload !== 'object' || Array.isArray(record.payload)) {
    throw new TypeError('Queue record requires a prepared payload');
  }
  if (!record.headers || typeof record.headers !== 'object' || Array.isArray(record.headers)) {
    throw new TypeError('Invalid queue record headers');
  }
  if (Object.keys(record.headers).some((key) => /^x-(altcha|recaptcha)-token$/i.test(key))) {
    throw new TypeError('Captcha tokens must not be persisted');
  }
  if (
    !record.captcha ||
    typeof record.captcha.required !== 'boolean' ||
    Object.keys(record.captcha).some((key) => !['required', 'provider'].includes(key))
  ) {
    throw new TypeError('Persist only captcha requirements');
  }
}

export function validateQueueRecordInput(input) {
  validateRecord({ ...input, status: 'pending' });
}

/**
 * The caller supplies a stable submissionId for one answering session and an
 * already masked payload. A new session must use a new submissionId, even for
 * the same survey. Captcha tokens are acquired only when sending.
 */
export function makeQueueRecord({
  submissionId,
  kind,
  nodeId,
  flowId,
  apiUrl,
  payload,
  headers = {},
  feedbackId = null,
  version = null,
  revision = 1,
  captcha = { required: false, provider: null },
}) {
  const now = Date.now();
  const record = {
    id: uuid(),
    submissionId,
    schemaVersion: SCHEMA_VERSION,
    kind,
    createdAt: now,
    updatedAt: now,
    status: 'pending',
    lastError: null,
    postStartedRevision: null,
    postStartedAt: null,
    nodeId,
    flowId,
    apiUrl,
    revision,
    payload,
    headers,
    feedbackId,
    version,
    captcha,
    ownership: null,
    // Diagnostics only; retries are never limited or scheduled by attempts.
    attempts: { count: 0, lastAt: null },
  };
  validateRecord(record);

  return record;
}

export async function createQueueRecord(input) {
  const record = makeQueueRecord(input);

  return withQueueStore('readwrite', async (store) => {
    await waitForRequest(store.add(record));
    return record;
  });
}

export function getQueueRecord(id) {
  return withQueueStore('readonly', (store) => waitForRequest(store.get(id)));
}

/** Reads only the supplied API environment, optionally narrowed to a session. */
export async function listQueueRecords(apiUrl, submissionId) {
  if (typeof apiUrl !== 'string' || !apiUrl.trim()) {
    throw new TypeError('Queue reads require an API environment');
  }
  return withQueueStore('readonly', (store) => {
    const index = submissionId === undefined ? 'byApiUrl' : 'bySubmission';
    const key = submissionId === undefined ? apiUrl : [apiUrl, submissionId];
    return waitForRequest(store.index(index).getAll(key));
  });
}

/**
 * Request changes advance the revision without resetting preparation metadata.
 * After a POST starts, only new incomplete answers may advance the revision.
 * Send results must use updateOwnedQueueRecord instead.
 */
export async function updateQueueRecord(id, changes) {
  if (Object.keys(changes).some((key) => !MUTABLE_FIELDS.includes(key))) {
    throw new TypeError('Cannot change queue record identity or schema');
  }
  return withQueueStore('readwrite', async (store) => {
    const record = await waitForRequest(store.get(id));
    if (!record) return undefined;

    const requestChanged = REQUEST_FIELDS.some((field) => Object.hasOwn(changes, field));
    const revision = changes.revision ?? (requestChanged ? record.revision + 1 : record.revision);

    if (Object.hasOwn(changes, 'revision') && (!Number.isInteger(changes.revision) || revision <= record.revision)) {
      throw new TypeError('Queue revision must increase');
    }

    if (hasPostStarted(record) && revision !== record.revision) {
      if (
        record.kind !== 'incomplete' ||
        !Object.hasOwn(changes, 'revision') ||
        !Array.isArray(changes.payload?.responses) ||
        JSON.stringify(changes.payload.responses) === JSON.stringify(record.payload.responses)
      ) {
        throw new TypeError('A started POST can only be followed by new incomplete answers');
      }
    }

    const updated = { ...record, ...changes, revision, updatedAt: Date.now() };

    // The new revision waits for the previous operation to release the session.
    if (
      revision !== record.revision &&
      (record.status === 'preparing' || hasPostStarted(record)) &&
      !Object.hasOwn(changes, 'status')
    ) {
      updated.status = 'pending';
    }
    assertPostCannotReopen(updated);
    validateRecord(updated);
    await waitForRequest(store.put(updated));
    return updated;
  });
}

/** Replace only unsent records; a started POST must keep its durable guard. */
export function deleteQueueRecord(id) {
  return withQueueStore('readwrite', async (store) => {
    const record = await waitForRequest(store.get(id));
    if (record?.postStartedRevision != null) return false;
    await waitForRequest(store.delete(id));
    return true;
  });
}

export function hasPostStarted(record) {
  return record.postStartedRevision != null && record.postStartedRevision >= record.revision;
}

function assertPostCannotReopen(record) {
  if (!hasPostStarted(record)) return;
  if (!['sending', 'blocked'].includes(record.status)) {
    throw new TypeError('A started POST cannot be retried');
  }
}

/** Claims preparation ownership; a started POST can never be reclaimed. */
export function claimQueueRecord(id) {
  return withQueueStore(
    'readwrite',
    async (store, owners, sessions) => {
      const record = await waitForRequest(store.get(id));
      if (!record || hasPostStarted(record) || !['pending', 'preparing'].includes(record.status)) return undefined;

      const session = await waitForRequest(sessions.get([record.apiUrl, record.submissionId]));
      if (record.kind === 'incomplete' && session?.finalized) return undefined;

      const current = await waitForRequest(owners.get([record.apiUrl, record.submissionId]));
      const now = Date.now();
      if (current?.expiresAt > now) return undefined;

      // Each acquisition gets a new token, including reacquisition by one worker.
      const ownership = { ownerId: uuid(), expiresAt: now + OWNERSHIP_DURATION_MS };
      const attempts = { count: (record.attempts?.count ?? 0) + 1, lastAt: now };
      const claimed = { ...record, status: 'preparing', ownership, attempts, updatedAt: now };
      await waitForRequest(
        owners.put({
          apiUrl: record.apiUrl,
          submissionId: record.submissionId,
          recordId: id,
          revision: record.revision,
          ...ownership,
        }),
      );
      await waitForRequest(store.put(claimed));
      return claimed;
    },
    true,
  );
}

function ownsSession(owner, record) {
  return owner && owner.recordId === record.id && owner.ownerId === record.ownership?.ownerId;
}

// Check and mutation share a transaction with ownership acquisition. A late
// response cannot pass this check and then race a new owner or answer revision.
async function getOwnedRecord(store, owners, attempt) {
  const current = await waitForRequest(store.get(attempt.id));

  if (!current || current.apiUrl !== attempt.apiUrl || current.submissionId !== attempt.submissionId) {
    return undefined;
  }

  const owner = await waitForRequest(owners.get([attempt.apiUrl, attempt.submissionId]));

  if (
    !ownsSession(owner, attempt) ||
    owner.expiresAt <= Date.now() ||
    owner.revision !== attempt.revision ||
    current.revision !== attempt.revision ||
    current.ownership?.ownerId !== owner.ownerId
  ) {
    return undefined;
  }
  return current;
}

/**
 * Call after preparation and immediately before the single feedback POST.
 * Only true authorizes sending, and only after this transaction commits.
 * A crash between this commit and the POST deliberately sacrifices delivery.
 */
export function beginQueueRecordPost(attempt) {
  return withQueueStore(
    'readwrite',
    async (store, owners, sessions) => {
      const session = await waitForRequest(sessions.get([attempt.apiUrl, attempt.submissionId]));
      if (session?.finalized && attempt.kind === 'incomplete') return false;

      const current = await getOwnedRecord(store, owners, attempt);
      if (!current || hasPostStarted(current) || current.status !== 'preparing') return false;

      const now = Date.now();
      await waitForRequest(
        store.put({
          ...current,
          status: 'sending',
          postStartedRevision: current.revision,
          postStartedAt: now,
          lastError: null,
          updatedAt: now,
        }),
      );
      return true;
    },
    true,
  );
}

/**
 * A started POST whose sender is gone can no longer report its result. Record it
 * as blocked with the caller's unknown-outcome diagnostics; it stays unsendable.
 * Web Locks callers must hold the session lock so a live sender is never touched.
 */
export function blockInterruptedQueuePost(id, lastError) {
  return withQueueStore(
    'readwrite',
    async (store, owners) => {
      const record = await waitForRequest(store.get(id));
      if (!record || !hasPostStarted(record) || record.status !== 'sending') return undefined;

      const owner = await waitForRequest(owners.get([record.apiUrl, record.submissionId]));
      if (owner?.expiresAt > Date.now()) return undefined;

      const updated = { ...record, status: 'blocked', lastError, ownership: null, updatedAt: Date.now() };

      assertPostCannotReopen(updated);
      validateRecord(updated);
      await waitForRequest(store.put(updated));

      return updated;
    },
    true,
  );
}

/** Returns undefined for an obsolete result; storage failures reject instead. */
export async function updateOwnedQueueRecord(attempt, changes) {
  if (Object.keys(changes).some((field) => !RESULT_FIELDS.includes(field))) {
    throw new TypeError('Send results can only update queue attempt metadata');
  }
  return withQueueStore(
    'readwrite',
    async (store, owners) => {
      const current = await getOwnedRecord(store, owners, attempt);
      if (!current) return undefined;

      const updated = { ...current, ...changes, updatedAt: Date.now() };
      assertPostCannotReopen(updated);
      validateRecord(updated);
      await waitForRequest(store.put(updated));
      return updated;
    },
    true,
  );
}

/** The sender may call this only after HTTP 200; false means its result is obsolete. */
export function deleteOwnedQueueRecord(attempt) {
  return withQueueStore(
    'readwrite',
    async (store, owners) => {
      const current = await getOwnedRecord(store, owners, attempt);
      if (!current) return false;

      await waitForRequest(store.delete(current.id));
      // Retain the session lease until the operation has fully settled.
      return true;
    },
    true,
  );
}

/** Expired owners cannot renew, including after another worker has taken over. */
export function renewQueueOwnership(record) {
  return withQueueStore(
    'readwrite',
    async (store, owners) => {
      const key = [record.apiUrl, record.submissionId];
      const owner = await waitForRequest(owners.get(key));
      const current = record.id ? await waitForRequest(store.get(record.id)) : undefined;
      const now = Date.now();
      if (!ownsSession(owner, record) || owner.expiresAt <= now) return undefined;

      const expiresAt = now + OWNERSHIP_DURATION_MS;
      await waitForRequest(owners.put({ ...owner, expiresAt }));
      if (current?.ownership?.ownerId === owner.ownerId) {
        await waitForRequest(store.put({ ...current, ownership: { ownerId: owner.ownerId, expiresAt } }));
      }
      return expiresAt;
    },
    true,
  );
}

/** Releases only this attempt; it cannot clear a replacement owner's lease. */
export function releaseQueueOwnership(record) {
  return withQueueStore(
    'readwrite',
    async (store, owners) => {
      const key = [record.apiUrl, record.submissionId];
      const owner = await waitForRequest(owners.get(key));
      if (!ownsSession(owner, record)) return false;

      const current = record.id ? await waitForRequest(store.get(record.id)) : undefined;
      if (current?.ownership?.ownerId === owner.ownerId) {
        const sameRevision = current.revision === record.revision && owner.revision === record.revision;

        await waitForRequest(
          store.put({
            ...current,
            status: sameRevision && current.status === 'preparing' ? 'pending' : current.status,
            ownership: null,
            updatedAt: sameRevision ? Date.now() : current.updatedAt,
          }),
        );
      }
      await waitForRequest(owners.delete(key));
      return true;
    },
    true,
  );
}
